import React, { useState, useRef, useEffect } from 'react';
import { FaRobot, FaTimes } from 'react-icons/fa';
import './ChatBot.css';
import { generateAIResponse } from '../../services/aiService';

const ChatBot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const toggleChat = () => {
        setIsOpen(!isOpen);
        if (!isOpen && messages.length === 0) {
            // Welcome message when chat is first opened
            setMessages([
                {
                    type: 'bot',
                    content: "Hello! I'm your Atlantic Primary Care assistant. How can I help you today?",
                    quickOptions: [
                        "Book an appointment",
                        "Office hours",
                        "Insurance questions",
                        "Contact information"
                    ]
                }
            ]);
        }
    };

    const handleQuickOption = async (option) => {
        setMessages(prev => [...prev, { type: 'user', content: option }]);
        setIsTyping(true);
        
        try {
            const response = await generateAIResponse(option);
            setMessages(prev => [...prev, { 
                type: 'bot', 
                content: response,
                quickOptions: ["Book an appointment", "Office hours", "Insurance questions", "Contact information"]
            }]);
        } catch (error) {
            setMessages(prev => [...prev, { 
                type: 'bot', 
                content: "I apologize, but I'm having trouble right now. Please call our office at 781-871-5030 for assistance.",
                quickOptions: ["Book an appointment", "Office hours", "Insurance questions", "Contact information"]
            }]);
        }
        
        setIsTyping(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!inputMessage.trim()) return;

        const userMessage = inputMessage.trim();
        setInputMessage('');
        setMessages(prev => [...prev, { type: 'user', content: userMessage }]);
        setIsTyping(true);

        try {
            const response = await generateAIResponse(userMessage);
            setMessages(prev => [...prev, { 
                type: 'bot', 
                content: response,
                quickOptions: ["Book an appointment", "Office hours", "Insurance questions", "Contact information"]
            }]);
        } catch (error) {
            setMessages(prev => [...prev, { 
                type: 'bot', 
                content: "I apologize, but I'm having trouble right now. Please call our office at 781-871-5030 for assistance.",
                quickOptions: ["Book an appointment", "Office hours", "Insurance questions", "Contact information"]
            }]);
        }

        setIsTyping(false);
    };

    return (
        <div className="chatbot-container">
            {isOpen && (
                <div className="chat-window">
                    <div className="chat-header">
                        <FaRobot className="robot-icon" />
                        <span>Atlantic Primary Care Assistant</span>
                        <button className="close-button" onClick={toggleChat}>
                            <FaTimes />
                        </button>
                    </div>
                    <div className="chat-messages">
                        {messages.map((message, index) => (
                            <div key={index} className={`message ${message.type}`}>
                                <div className="message-content">{message.content}</div>
                                {message.type === 'bot' && message.quickOptions && (
                                    <div className="quick-options">
                                        {message.quickOptions.map((option, optIndex) => (
                                            <button
                                                key={optIndex}
                                                onClick={() => handleQuickOption(option)}
                                                className="quick-option-button"
                                            >
                                                {option}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                        {isTyping && (
                            <div className="message bot">
                                <div className="typing-indicator">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                    <form onSubmit={handleSubmit} className="chat-input">
                        <input
                            type="text"
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            placeholder="Type your message..."
                        />
                        <button type="submit">Send</button>
                    </form>
                </div>
            )}
            <button className="toggle-button" onClick={toggleChat}>
                <FaRobot />
            </button>
        </div>
    );
};

export default ChatBot; 